import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users, UserPlus, Loader2, UserCog, Trash2, AlertTriangle } from "lucide-react";

export function CommunityMembersDialog({
  open, onOpenChange, communityMembers, invitations,
  onInviteUser, onUpgradeMember, upgradingMember, onDeleteMember
}) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (member) => {
    setDeleting(member.user_id);
    try {
      await onDeleteMember(member.user_id);
      setConfirmDelete(null);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <UserCog className="w-5 h-5 mr-2 text-purple-600" />
                Community Members Management
              </div>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={onInviteUser}>
                <UserPlus className="w-4 h-4 mr-1" /> Invite User
              </Button>
            </DialogTitle>
            <DialogDescription>Manage community members and upgrade them to client plans</DialogDescription>
          </DialogHeader>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 py-4">
            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-700">{communityMembers.stats?.total_community_access || 0}</p>
              <p className="text-xs text-purple-600">Total Members</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-700">{communityMembers.stats?.community_only || 0}</p>
              <p className="text-xs text-blue-600">Community Only</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700">{communityMembers.stats?.clients_with_access || 0}</p>
              <p className="text-xs text-green-600">Clients</p>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-amber-700">{invitations.length}</p>
              <p className="text-xs text-amber-600">Pending Invites</p>
            </div>
          </div>

          {/* Members List */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {communityMembers.members?.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-neutral-300" />
                <p>No community members yet</p>
                <Button className="mt-3 bg-green-600 hover:bg-green-700" onClick={onInviteUser}>
                  <UserPlus className="w-4 h-4 mr-2" /> Invite Your First Member
                </Button>
              </div>
            ) : (
              communityMembers.members?.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg group" data-testid={`community-member-${member.user_id}`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${member.member_type === "client" ? "bg-green-100" : "bg-purple-100"}`}>
                      <span className={`font-semibold ${member.member_type === "client" ? "text-green-700" : "text-purple-700"}`}>
                        {member.name?.charAt(0) || "?"}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">{member.name}</p>
                      <p className="text-sm text-neutral-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {member.member_type === "client" ? (
                      <Badge className="bg-green-100 text-green-700">Client - {member.tier || "Basic"}</Badge>
                    ) : (
                      <>
                        <Badge className="bg-purple-100 text-purple-700">Community Only</Badge>
                        <Select onValueChange={(tier) => onUpgradeMember(member.user_id, tier)}>
                          <SelectTrigger className="w-32 h-8 text-xs" data-testid={`upgrade-member-${member.user_id}`}>
                            <SelectValue placeholder="Upgrade to..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="premium_rapid">R.A.P.I.D.</SelectItem>
                            <SelectItem value="podcast_academy">Podcast Academy</SelectItem>
                            <SelectItem value="growth_strategic">Strategic</SelectItem>
                            <SelectItem value="growth_optimization">Optimization</SelectItem>
                            <SelectItem value="growth_scale">Scale</SelectItem>
                          </SelectContent>
                        </Select>
                        {upgradingMember === member.user_id && <Loader2 className="w-4 h-4 animate-spin" />}
                      </>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      className="h-8 w-8 p-0 text-neutral-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setConfirmDelete(member)}
                      data-testid={`delete-member-${member.user_id}`}
                      title="Remove from portal"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" /> Remove Member
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{confirmDelete?.name}</strong> ({confirmDelete?.email}) from the portal
              {confirmDelete?.member_type === "client" && " and all their client data including projects, notes, and reports"}.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(confirmDelete)} disabled={deleting === confirmDelete?.user_id} data-testid="confirm-delete-member-btn">
              {deleting === confirmDelete?.user_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
